import { Injectable } from '@angular/core';

import jspreadsheet from 'jspreadsheet';
import jSuites from 'jsuites';

import { ColumnCoords } from '@dpr-details/enums';
import { dynamicTextPlaceholder, StaticMessage } from '@shared/enums';

import {
  Constants,
  convertTimeToDate,
  convertToDateTimeWithoutTimeZoneSuffix,
  getStaticMessage,
  isDateBeforeEpoch,
  parseTimeString,
} from '@shared/utils';
import {
  dateRegex,
  dateTimeRegex,
  findByValue,
  formatTimeToDisplayString,
  parseCustomDataInfoFromColumnName,
  readOnlyPlaceholder,
  isCellReadonly,
} from '@dpr-details/utils/import-activities.utils';

import { DataType, SelectionItem } from '@shared/models';
import { ImportActivitiesData } from '@dpr-details/models';
import { ActivityGroup } from '@shared/models/project-setup/activity-group.model';
import { CustomData, Activity, Location } from '@shared/models/project-setup';

@Injectable()
export class ImportDprActivityValidation {
  invalidColumnError = $localize`:@@dprDetails.dprImportActivitiesDialog.errInvalidColumnError:Invalid (---)`;

  public getValidations(
    data: ImportActivitiesData,
    normalizedPasteData: string[][]
  ): jspreadsheet.Validation[] {
    /**
     * this.x	- column index
     * this.y	- row index
     * this.w	- worksheet instance
     *
     * https://jspreadsheet.com/docs/validations
     */

    const validationHelpers: { [key: string]: (...args: any) => any } = {
      validateActivityGroupValue: (
        value: string,
        rowIndex: number,
        columnIndex: number,
        worksheet: jspreadsheet.worksheetInstance,
        activityGroupOptions: ActivityGroup[]
      ) =>
        this.validateActivityGroupValue(
          value,
          rowIndex,
          columnIndex,
          worksheet,
          activityGroupOptions
        ),
      validateActivityValue: (
        value: string,
        rowIndex: number,
        pasteData: string[],
        columnIndex: number,
        worksheet: jspreadsheet.worksheetInstance,
        data: ImportActivitiesData
      ) =>
        this.validateActivityValue(
          value,
          rowIndex,
          pasteData,
          columnIndex,
          worksheet,
          data
        ),
      validateLocationValue: (
        locationValue: string,
        rowIndex: number,
        pasteData: string[],
        columnIndex: number,
        worksheet: jspreadsheet.worksheetInstance,
        data: ImportActivitiesData
      ) =>
        this.validateLocationValue(
          locationValue,
          rowIndex,
          pasteData,
          columnIndex,
          worksheet,
          data
        ),
      validateStartValue: (
        value: string,
        rowIndex: number,
        pasteData: string[],
        columnIndex: number,
        worksheet: jspreadsheet.worksheetInstance
      ) =>
        this.validateStartValue(
          value,
          rowIndex,
          pasteData,
          columnIndex,
          worksheet
        ),
      validateFinishValue: (
        value: string,
        rowIndex: number,
        pasteData: string[],
        columnIndex: number,
        worksheet: jspreadsheet.worksheetInstance
      ) =>
        this.validateFinishValue(
          value,
          rowIndex,
          pasteData,
          columnIndex,
          worksheet
        ),
      validateCommentValue: (
        value: string,
        rowIndex: number,
        columnIndex: number,
        worksheet: jspreadsheet.worksheetInstance
      ) => this.validateCommentValue(value, rowIndex, columnIndex, worksheet),
      validateCustomDataValue: (
        value: string,
        columnIndex: number,
        rowIndex: number,
        worksheet: jspreadsheet.worksheetInstance,
        data: ImportActivitiesData
      ) =>
        this.validateCustomDataValue(
          value,
          columnIndex,
          rowIndex,
          worksheet,
          data
        ),
      getPastedRowIndex: (value: any, normalizedPasteData: string[][]) =>
        this.getPastedRowIndex(value, normalizedPasteData),
      getValidationError: (...placeholders: string[]) =>
        this.getValidationError(this.invalidColumnError, ...placeholders),
    };

    let columnNameForValidation = 'G';

    (jSuites.validations as any).isActivityGroup = function (
      value: string | number
    ) {
      if (!String.isNullOrWhiteSpace(value?.toString())) {
        return validationHelpers.validateActivityGroupValue(
          value,
          this.y,
          this.x,
          this.w,
          data.getActivityGroups()
        );
      }

      return true;
    };

    (jSuites.validations as any).isActivity = function (
      value: string | number
    ) {
      if (!String.isNullOrWhiteSpace(value?.toString())) {
        const pastedIndex = validationHelpers.getPastedRowIndex(
          value,
          normalizedPasteData
        );

        return validationHelpers.validateActivityValue(
          value,
          this.y,
          pastedIndex != -1 ? normalizedPasteData[pastedIndex] : [],
          this.x,
          this.w,
          data
        );
      }

      return true;
    };

    (jSuites.validations as any).isLocation = function (
      value: string | number
    ) {
      if (!String.isNullOrWhiteSpace(value?.toString())) {
        const pastedIndex = validationHelpers.getPastedRowIndex(
          value,
          normalizedPasteData
        );

        return validationHelpers.validateLocationValue(
          value,
          this.y,
          pastedIndex != -1 ? normalizedPasteData[pastedIndex] : [],
          this.x,
          this.w,
          data
        );
      }

      return true;
    };

    (jSuites.validations as any).isStartTime = function (
      value: string | number
    ) {
      if (!String.isNullOrWhiteSpace(value?.toString())) {
        const pastedIndex = validationHelpers.getPastedRowIndex(
          value,
          normalizedPasteData
        );

        return validationHelpers.validateStartValue(
          formatTimeToDisplayString(value.toString()),
          this.y,
          pastedIndex != -1 ? normalizedPasteData[pastedIndex] : [],
          this.x,
          this.w
        );
      }

      return true;
    };

    (jSuites.validations as any).isFinishTime = function (
      value: string | number
    ) {
      if (!String.isNullOrWhiteSpace(value?.toString())) {
        const pastedIndex = validationHelpers.getPastedRowIndex(
          value,
          normalizedPasteData
        );

        return validationHelpers.validateFinishValue(
          formatTimeToDisplayString(value.toString()),
          this.y,
          pastedIndex != -1 ? normalizedPasteData[pastedIndex] : [],
          this.x,
          this.w
        );
      }

      return true;
    };

    (jSuites.validations as any).isComment = function (value: string | number) {
      if (!String.isNullOrWhiteSpace(value?.toString())) {
        return validationHelpers.validateCommentValue(
          value,
          this.y,
          this.x,
          this.w
        );
      }

      return true;
    };

    (jSuites.validations as any).isCustomData = function (value: any) {
      if (!String.isNullOrWhiteSpace(value?.toString())) {
        return validationHelpers.validateCustomDataValue(
          value,
          this.x,
          this.y,
          this.w,
          data
        );
      }

      return true;
    };

    return [
      {
        range: `${data.worksheetName}!A:A`,
        action: 'warning',
        text: validationHelpers.getValidationError(
          getStaticMessage(StaticMessage.ActivityGroupColumn)
        ),
        type: 'isActivityGroup' as any,
        criteria: 'between',
        dropdown: true,
      },
      {
        range: `${data.worksheetName}!B:B`,
        action: 'warning',
        text: validationHelpers.getValidationError(
          getStaticMessage(StaticMessage.ActivityColumn)
        ),
        type: 'isActivity' as any,
        criteria: 'between',
        dropdown: true,
      },
      {
        range: `${data.worksheetName}!C:C`,
        action: 'warning',
        text: validationHelpers.getValidationError(
          getStaticMessage(StaticMessage.LocationColumn)
        ),
        type: 'isLocation' as any,
        criteria: 'between',
        dropdown: true,
      },
      {
        range: `${data.worksheetName}!D:D`,
        action: 'warning',
        text: validationHelpers.getValidationError(
          getStaticMessage(StaticMessage.StartColumn)
        ),
        type: 'isStartTime' as any,
        criteria: 'between',
        dropdown: true,
      },
      {
        range: `${data.worksheetName}!E:E`,
        action: 'warning',
        text: validationHelpers.getValidationError(
          getStaticMessage(StaticMessage.FinishColumn)
        ),
        type: 'isFinishTime' as any,
        criteria: 'between',
        dropdown: true,
      },
      {
        range: `${data.worksheetName}!F:F`,
        action: 'warning',
        text: validationHelpers.getValidationError(
          getStaticMessage(StaticMessage.CommentColumn)
        ),
        type: 'isComment' as any,
        criteria: 'between',
        dropdown: true,
      },
      ...data.inlineCustomData.reduce((acc, x) => {
        acc.push({
          range: `${data.worksheetName}!${columnNameForValidation}:${columnNameForValidation}`,
          action: 'warning' as any,
          text: validationHelpers.getValidationError(x.name),
          type: 'isCustomData' as any,
          criteria: 'between' as any,
          dropdown: true as any,
        });
        columnNameForValidation = String.fromCharCode(
          columnNameForValidation.charCodeAt(0) + 1
        );

        return acc;
      }, []),
    ];
  }

  public getValidationError(
    errorMessage: string,
    ...placeholders: string[]
  ): string {
    placeholders.forEach((placeholder) => {
      errorMessage = errorMessage.replace(dynamicTextPlaceholder, placeholder);
    });

    return errorMessage;
  }

  private validateActivityGroupValue(
    value: string,
    rowIndex: number,
    columnIndex: number,
    worksheet: jspreadsheet.worksheetInstance,
    activityGroupOptions: ActivityGroup[]
  ): boolean {
    if (value === readOnlyPlaceholder) {
      return isCellReadonly(worksheet, columnIndex, rowIndex);
    }

    return !!findByValue<ActivityGroup>(activityGroupOptions, value);
  }

  private validateActivityValue(
    value: string,
    rowIndex: number,
    pasteData: string[],
    columnIndex: number,
    worksheet: jspreadsheet.worksheetInstance,
    data: ImportActivitiesData
  ): boolean {
    if (value === readOnlyPlaceholder) {
      return isCellReadonly(worksheet, columnIndex, rowIndex);
    }

    // Get ActivityGroup value from the same row
    const activityGroupValue = this.getCorrespondingValue(
      ColumnCoords.ActivityGroup,
      rowIndex,
      pasteData,
      columnIndex - 1,
      worksheet
    );

    if (!activityGroupValue) {
      return false;
    }

    const activityGroup = findByValue<ActivityGroup>(
      data.getActivityGroups(),
      activityGroupValue
    );
    if (!activityGroup) {
      return false; // Activity group validation is handled separately
    }

    if (!findByValue<Activity>(data.getActivities(activityGroup.id), value)) {
      return false;
    }

    return true;
  }

  private validateLocationValue(
    locationValue: string,
    rowIndex: number,
    pasteData: string[],
    columnIndex: number,
    worksheet: jspreadsheet.worksheetInstance,
    data: ImportActivitiesData
  ): boolean {
    if (locationValue === readOnlyPlaceholder) {
      return isCellReadonly(worksheet, columnIndex, rowIndex);
    }

    // Get ActivityGroup and Activity values from the same row
    const activityGroupValue = this.getCorrespondingValue(
      ColumnCoords.ActivityGroup,
      rowIndex,
      pasteData,
      columnIndex - 2,
      worksheet
    );

    const activityValue = this.getCorrespondingValue(
      ColumnCoords.Activity,
      rowIndex,
      pasteData,
      columnIndex - 1,
      worksheet
    );

    if (!activityGroupValue || !activityValue) {
      return false;
    }

    const activityGroup = findByValue<ActivityGroup>(
      data.getActivityGroups(),
      activityGroupValue
    );

    if (!activityGroup) {
      return false; // Activity group validation is handled separately
    }

    const selectedActivity = findByValue<Activity>(
      data.getActivities(activityGroup.id),
      activityValue
    );
    if (!selectedActivity) {
      return false; // Activity validation is handled separately
    }

    if (
      !findByValue<Location>(
        data.getLocations(selectedActivity.id),
        locationValue
      )
    ) {
      return false;
    }

    return true;
  }

  private validateStartValue(
    value: string,
    rowIndex: number,
    pasteData: string[],
    columnIndex: number,
    worksheet: jspreadsheet.worksheetInstance
  ): boolean {
    if (value === readOnlyPlaceholder) {
      return isCellReadonly(worksheet, columnIndex, rowIndex);
    }

    // Check if time format is valid
    if (!parseTimeString(value)) {
      return false;
    }

    const startTime = convertTimeToDate(value);
    if (!startTime) {
      return false;
    }

    const finishValue = this.getCorrespondingValue(
      ColumnCoords.FinishColumn,
      rowIndex,
      pasteData,
      columnIndex + 1,
      worksheet
    );

    const finishTime = convertTimeToDate(
      formatTimeToDisplayString(finishValue)
    );

    if (!finishValue) {
      return true; // No finish date to compare with
    }

    if (startTime > finishTime) {
      return false;
    }

    return true;
  }

  private validateFinishValue(
    value: string,
    rowIndex: number,
    pasteData: string[],
    columnIndex: number,
    worksheet: jspreadsheet.worksheetInstance
  ): boolean {
    if (value === readOnlyPlaceholder) {
      return isCellReadonly(worksheet, columnIndex, rowIndex);
    }

    // Check if time format is valid
    if (!parseTimeString(value)) {
      return false;
    }

    const finishTime = convertTimeToDate(value);
    if (!finishTime) {
      return false;
    }

    const startValue = this.getCorrespondingValue(
      ColumnCoords.StartColumn,
      rowIndex,
      pasteData,
      columnIndex - 1,
      worksheet
    );

    const startTime = convertTimeToDate(formatTimeToDisplayString(startValue));

    if (!startTime) {
      return true; // No start date to compare with
    }

    if (startTime > finishTime) {
      return false;
    }

    return true;
  }

  private validateCommentValue(
    value: string,
    rowIndex: number,
    columnIndex: number,
    worksheet: jspreadsheet.worksheetInstance
  ): boolean {
    if (value === readOnlyPlaceholder) {
      return isCellReadonly(worksheet, columnIndex, rowIndex);
    }

    const commentLength = value?.toString().trim().length || 0;

    return commentLength > Constants.FieldLimits.maxGridCommentLength
      ? false
      : true;
  }

  private validateCustomDataValue(
    value: string | number,
    columnIndex: number,
    rowIndex: number,
    worksheet: jspreadsheet.worksheetInstance,
    data: ImportActivitiesData
  ): boolean {
    if (value === readOnlyPlaceholder) {
      return isCellReadonly(worksheet, columnIndex, rowIndex);
    }

    const column = worksheet.getColumn(columnIndex);
    const customDataInfo = parseCustomDataInfoFromColumnName(column.name);

    switch (customDataInfo.dataTypeId) {
      case DataType.Date:
        return this.isValidDateString(value);
      case DataType.Datetime:
        return this.isValidDateTimeString(value);
      case DataType.FreeText:
        return this.validateFreeTextValue(value);
      case DataType.Percentage:
        return this.isValidPercentageString(value);
      case DataType.Number:
        return this.isValidNumber(
          value,
          customDataInfo.customDataId,
          data.inlineCustomData
        );
      case DataType.Boolean:
      case DataType.List:
        const customData = data.inlineCustomData.find(
          (cd) => cd.id === customDataInfo.customDataId
        );
        return this.isValidDropdownString(value, customData, data);
      default:
        return null;
    }
  }

  private isValidDateTimeString(dateString: number | string): boolean {
    const match = dateString.toString().match(dateTimeRegex);
    if (!match) return false;

    const [, dayStr, monthStr, yearStr, hourStr, minuteStr] = match;

    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);
    const hour = Number(hourStr);
    const minute = Number(minuteStr);

    // Basic numeric checks
    if (
      year < 1000 ||
      !(day >= 1 && day <= 31) ||
      !(month >= 1 && month <= 12) ||
      !(hour >= 0 && hour < 24) ||
      !(minute >= 0 && minute <= 59)
    ) {
      return false;
    }

    // Construct date
    const date = new Date(year, month - 1, day, hour, minute);

    // Detect invalid calendar dates (31-02, 31-04 etc.)
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getHours() !== hour ||
      date.getMinutes() !== minute ||
      isDateBeforeEpoch(date)
    ) {
      return false;
    }

    return !!convertToDateTimeWithoutTimeZoneSuffix(
      date,
      date.getHours(),
      date.getMinutes()
    );
  }

  private isValidDateString(dateString: string | number): boolean {
    const match = dateString.toString().match(dateRegex);
    if (match === null) {
      return false;
    }

    const [, dayStr, monthStr, yearStr] = match;

    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);

    // Basic numeric checks
    if (
      year < 1000 ||
      !(day >= 1 && day <= 31) ||
      !(month >= 1 && month <= 12)
    ) {
      return false;
    }

    // Construct date
    const date = new Date(year, month - 1, day);

    // Detect invalid calendar dates (31-02, 31-04 etc.)
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      isDateBeforeEpoch(date)
    ) {
      return false;
    }

    return !!convertToDateTimeWithoutTimeZoneSuffix(date);
  }

  private validateFreeTextValue(value: string | number): boolean {
    const freeTextLength = value?.toString().trim().length || 0;

    return freeTextLength > Constants.FieldLimits.maxFreeTextDataTypeLimit
      ? false
      : true;
  }

  private isValidNumber(
    value: string | number,
    customDataId: number,
    inlineCustomData: CustomData[]
  ): boolean {
    // Handle empty or null values
    if (String.isNullOrWhiteSpace(value?.toString())) {
      return true; // Consider empty as valid (optional field)
    }

    // Convert to string for validation
    const stringValue = value.toString().trim();

    // Check if it's a valid number (including decimals)
    const numberRegex = /^(\d+\.?\d*|\.\d+)$/;

    if (!numberRegex.test(stringValue)) {
      return false; // Invalid number format
    }

    const customData = inlineCustomData.find((cd) => cd.id === customDataId);

    if (customData?.decimalCount !== undefined) {
      const decimalPlaces = (stringValue.split('.')[1] || '').length;
      return decimalPlaces <= customData.decimalCount;
    }

    return true;
  }

  private isValidPercentageString(percentage: string | number): boolean {
    const numericValue =
      typeof percentage === 'number'
        ? percentage
        : Number(percentage.toString().replace('%', '').trim());

    return !isNaN(numericValue) && numericValue >= 0 && numericValue <= 100;
  }

  private isValidDropdownString(
    value: string | number,
    customData: CustomData,
    data: ImportActivitiesData
  ): boolean {
    return !!findByValue<SelectionItem>(
      data.getSelectionOptions(customData),
      value.toString()
    );
  }

  private getPastedRowIndex(
    value: string | number,
    normalizedPasteData: string[][]
  ): number {
    let pastedIndex = -1;
    if (normalizedPasteData && normalizedPasteData.length > 0) {
      for (let index = 0; index < normalizedPasteData.length; index++) {
        if (normalizedPasteData[index].indexOf(value.toString()) !== -1) {
          pastedIndex = index;
          break;
        }
      }
    }

    return pastedIndex;
  }

  private getCorrespondingValue(
    targetColumn: number,
    targetRow: number,
    pasteData: string[],
    columnIndex: number,
    worksheet: jspreadsheet.worksheetInstance
  ): string {
    const targetValue =
      worksheet.getValueFromCoords(targetColumn, targetRow) || '';

    if (targetValue) {
      return targetValue;
    }

    if (pasteData.length > columnIndex) {
      return pasteData[columnIndex] || '';
    }

    return '';
  }
}
